import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { Cell, Pie, PieChart, Tooltip as RTooltip } from 'recharts';
import * as R from 'ramda';
import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import { shape } from 'prop-types';

import CustomMaxTooltip from './CustomMaxTooltip';

const MaxDonut = ({ data }) => {
  const maxAngle = 270;
  const getSome = arr => arr.reduce((a, b) => a + b, 0);
  const totals = getSome(R.path(['projections', 'total'], data).map(({ value }) => value));
  const overAllocation = R.prop('overAllocation', data);

  const calcAngle = () => {
    const maxVol = getSome(R.path(['projections', 'capacity'], data).map(({ value }) => value));
    const percent = maxAngle * (totals / maxVol);
    return maxAngle - percent;
  };

  const calcFill = R.cond([
    [R.equals('Max'), R.always('#213657')],
    [R.equals('Contract Max'), R.always('rgba(33, 54, 87, 0.25)')],
    [R.equals('In Transit'), R.always('#80CBC4')],
    [R.equals('Available Reserved'), R.always('#004D40')],
    [R.equals('In Transit Reserved'), R.always('#90CAF9')],
    [R.equals('Available'), R.always('#0D47A1')],
  ]);

  return (
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    <Box sx={{ mb: { xs: 3, lg: 0 }, mr: { xs: 0, lg: 19 }, position: 'relative', width: 344 }}>
      <PieChart height={344} width={344}>
        <Pie
          cx={172}
          cy={172}
          data={R.reverse(R.path(['projections', 'capacity'], data))}
          dataKey="value"
          endAngle={0}
          innerRadius={135}
          outerRadius={165}
          paddingAngle={2}
          startAngle={maxAngle}
        >
          {R.reverse(R.path(['projections', 'capacity'], data)).map(({ name }) => (
            <Cell fill={calcFill(name)} key={name} />
          ))}
        </Pie>
        <Pie
          cx={172}
          cy={172}
          data={R.reverse(R.path(['projections', 'total'], data))}
          dataKey="value"
          endAngle={calcAngle()}
          innerRadius={95}
          outerRadius={125}
          paddingAngle={2}
          startAngle={maxAngle}
        >
          {R.reverse(R.path(['projections', 'total'], data)).map(({ name }) => (
            <Cell fill={overAllocation ? '#B71C1C' : calcFill(name)} key={name} />
          ))}
        </Pie>
        <RTooltip
          content={<CustomMaxTooltip />}
          tooltipData={R.prop('tooltips', data)}
          wrapperStyle={{ zIndex: 1000 }}
        />
      </PieChart>
      <Stack
        sx={{
          height: 134,
          left: '27%',
          p: 1,
          position: 'absolute',
          top: '34%',
          width: 170,
        }}
      >
        <Stack alignItems="center">
          <Typography variant="subtitle2">Current Total Vol:</Typography>
          <Typography color={overAllocation ? 'error.main' : 'text.primary'} variant="h6">
            {formatNumberWithoutDecimals(totals)} bbl
          </Typography>
          {overAllocation && (
            <Stack alignItems="center">
              <Typography color="text.primary" variant="caption">
                Excess Volume
              </Typography>
              <Typography color="error.main" variant="caption">
                {formatNumberWithoutDecimals(R.prop('excessVolume', data))} bbl
              </Typography>
            </Stack>
          )}
        </Stack>
        <Stack alignItems="center">
          <Typography color="text.primary" variant="caption">
            Current Maximum:
            <Tooltip
              placement="bottom"
              slotProps={{
                popper: { sx: { maxWidth: 200, p: 1, textAlign: 'center' } },
              }}
              title="Current Max is updated every Monday at midnight."
            >
              <InfoRoundedIcon
                sx={{
                  '&:hover': { cursor: 'pointer' },
                  color: 'graphic.dark',
                  fontSize: 16,
                  mt: 0.5,
                }}
              />
            </Tooltip>
          </Typography>
          <Typography variant="body2">
            {formatNumberWithoutDecimals(R.prop('currentMax', data))} bbl
          </Typography>
        </Stack>
      </Stack>
      <Typography
        sx={{ fontWeight: 700, position: 'absolute', right: 0, top: '49%' }}
        variant="body2"
      >
        ---------
      </Typography>
      <Typography
        sx={{ bottom: 45, fontWeight: 700, left: '54%', position: 'absolute' }}
        variant="body1"
      >
        Max Total
      </Typography>
      <Typography
        sx={{ bottom: 6, fontWeight: 700, left: '54%', position: 'absolute' }}
        variant="body1"
      >
        Max Capacity
      </Typography>
    </Box>
  );
};

MaxDonut.propTypes = {
  data: shape({}),
};

MaxDonut.defaultProps = {
  data: null,
};

export default MaxDonut;
